import { NoteEntityId, NoteId } from "@sp-crm/core";
import { IEntityNote } from "components/notes/edit";
import {
    GetNotesForEntityQuery,
    UpdateNoteMutation,
    UpdateNoteMutationVariables,
    useDeleteNoteMutation,
    useUpdateNoteMutation,
} from "generated/graphql";
import React, { useCallback, useMemo } from "react";

export type NoteRowNote = GetNotesForEntityQuery["getNotesForEntity"][0];

interface UseNoteRowArgs {
    note: NoteRowNote;
    entityId: NoteEntityId;
    refetch: () => void;
    onDeleted: (noteId: NoteId) => void;
    onUpdated: (note: UpdateNoteMutation["updateNote"]) => void;
}

export const useNoteRow = (args: UseNoteRowArgs) => {
    const { note, entityId, refetch, onDeleted, onUpdated } = args;
    const [isEditing, setIsEditing] = React.useState(false);
    const [isDeleting, setIsDeleting] = React.useState(false);
    const enableEdit = useCallback(() => setIsEditing(true), []);
    const disableEdit = useCallback(() => setIsEditing(false), []);
    const deleteMutation = useDeleteNoteMutation();
    const deleteCallback = useCallback(async () => {
        setIsDeleting(true);
        await deleteMutation.mutateAsync({
            noteId: note._id,
            entityType: "dontcare",
            entityId,
        });
        setIsDeleting(false);
        onDeleted(note._id);
        refetch();
    }, [deleteMutation, note._id, entityId, refetch, onDeleted]);
    const updateMutation = useUpdateNoteMutation();
    const updateCallback = useCallback(
        async (noteId: NoteId, text: string, dateAdded: Date, isPinned: boolean) => {
            const payload: UpdateNoteMutationVariables = {
                entityId,
                noteId: noteId,
                params: {
                    text: text,
                    isPublished: true,
                    dateAddedOverride: dateAdded,
                    isPinned,
                },
            };
            const result = await updateMutation.mutateAsync(payload);
            onUpdated(result.updateNote);
            disableEdit();
            refetch();
        },
        [updateMutation, entityId, disableEdit, refetch, onUpdated],
    );
    const editableNote: IEntityNote = useMemo(() => {
        return {
            _id: note._id,
            authorId: note.authorId,
            text: note.text,
            dateAdded: new Date(note.dateAdded),
            dateAddedManualOverride: new Date(note.dateAdded),
            wasEdited: note.wasEdited,
            isPinned: note.isPinned,
            editorId: note.editorId,
            updatedAt: new Date(note.updatedAt),
        };
    }, [note]);

    return {
        isEditing,
        isDeleting,
        enableEdit,
        disableEdit,
        deleteCallback,
        updateCallback,
        editableNote,
    };
};
